/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useState } from 'react';
import './CrceSelect.css';

function resolveOptionValue(raw, options) {
  if (raw === '') return undefined;
  const match = options.find((o) => String(o.value) === raw);
  return match ? match.value : raw;
}

/**
 * Native select styled to match @crc-evans/ui-components Input (teal focus ring).
 * The ui-components package does not ship a dropdown Select; this keeps shipment forms consistent.
 */
export default function CrceSelect({
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  loading = false,
  allowClear = false,
  size = 'medium',
  fullWidth = true,
  className = '',
  id,
  name,
  style,
  onDropdownVisibleChange,
  'aria-invalid': ariaInvalid,
}) {
  const [focused, setFocused] = useState(false);

  const normalizedOptions = useMemo(
    () => options.map((o) => ({ label: o.label, value: o.value })),
    [options],
  );

  const handleChange = useCallback(
    (e) => {
      const raw = e.target.value;
      const next = resolveOptionValue(raw, normalizedOptions);
      onChange?.(next);
    },
    [onChange, normalizedOptions],
  );

  const strVal = value === undefined || value === null ? '' : String(value);

  const wrapClass = [
    'crce-select-field__wrap',
    focused && 'crce-select-field__wrap--focused',
    (disabled || loading) && 'crce-select-field__wrap--disabled',
    ariaInvalid && 'crce-select-field__wrap--error',
  ]
    .filter(Boolean)
    .join(' ');

  const rootClass = [
    'crce-select-field',
    `crce-select-field--${size}`,
    fullWidth && 'crce-select-field--full-width',
    loading && 'crce-select-field--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const showPlaceholder = placeholder !== undefined && placeholder !== null && placeholder !== '';

  return (
    <div className={rootClass} style={style}>
      <div className={wrapClass}>
        <select
          id={id}
          name={name}
          className={`crce-select-field__control crce-select-field__control--${size}`}
          value={strVal}
          onChange={handleChange}
          onFocus={() => {
            setFocused(true);
            onDropdownVisibleChange?.(true);
          }}
          onBlur={() => {
            setFocused(false);
            onDropdownVisibleChange?.(false);
          }}
          disabled={disabled || loading}
          aria-busy={loading || undefined}
          aria-invalid={ariaInvalid || undefined}
        >
          {showPlaceholder && (
            <option value="" disabled={!allowClear} hidden={!allowClear}>
              {placeholder}
            </option>
          )}
          {normalizedOptions.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
